import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Heart, Calendar } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface StampEntry {
  id: string
  created_at: string
  points_awarded: number
}

interface VisitStampHistoryProps {
  customerId: string
  tenantId: string
}

const VisitStampHistory = ({ customerId, tenantId }: VisitStampHistoryProps) => {
  const { toast } = useToast()
  const [stamps, setStamps] = useState<StampEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchVisitStamps()
  }, [customerId, tenantId])

  const fetchVisitStamps = async () => {
    try {
      const { data, error } = await supabase
        .from('stamps')
        .select('*')
        .eq('customer_id', customerId)
        .eq('tenant_id', tenantId)
        .eq('stamp_type', 'visit')
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      setStamps(data || [])
    } catch (error) {
      console.error('Error fetching visit stamps:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Besuchshistorie konnte nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-4">Wird geladen...</div>
  }

  if (stamps.length === 0) {
    return (
      <div className="text-center py-8">
        <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Noch keine Besuche</h3>
        <p className="text-muted-foreground">
          Besuchen Sie uns und sammeln Sie Ihre ersten Besuchsstempel!
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {stamps.map((stamp) => (
        <div key={stamp.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Besuch registriert</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(stamp.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
              </div>
            </div>
          </div>
          <Badge variant="default">
            +{stamp.points_awarded} Punkt{stamp.points_awarded !== 1 ? 'e' : ''}
          </Badge>
        </div>
      ))}
    </div>
  )
}

export default VisitStampHistory